// Bespoke voxel prefabs for unit kinds that previously fell back to
// placeholder mappings (e.g. settler → wizard, guard → orc). Each prefab
// is a compact 8-30 voxel model designed to be visually distinct in the
// instanced voxel renderer at the 1/16-unit scale used by VoxelUnit.
//
// Voxel coordinate convention (matches adventure-craft): origin at the
// unit's feet on the tile surface. Y is up. X is right. Z is forward
// (facing camera). Humanoid units stand roughly 14-18 voxels tall.

import { archmage, chaosLord, mage, templar, treant, wagon } from './creatures_extra_arcane.js';
import { bronzeSmith, engineer, explorer, settler } from './creatures_extra_civil.js';
import { bloodPriest, demon, spy, zealot } from './creatures_extra_dark.js';
import { darkTemplar, luminarch, seraphim } from './creatures_extra_holy.js';
import {
  bowman,
  catapult,
  cleric,
  guard,
  knight,
  paladin,
  soldier,
} from './creatures_extra_military.js';
import type { CharacterPrefab } from './types.js';

/**
 * Convert the named parts of a prefab into team voxels (BlockVoxel.team),
 * keeping each voxel's light/shade: the original colour's luminance becomes
 * the grey the renderer multiplies the owner's colour by. Detail survives —
 * a templar's red cross on a white surcoat becomes a darker team shade on a
 * lighter one. Voxels with metal/emissive properties are left as they are.
 */
function withTeamParts(prefab: CharacterPrefab, partNames: readonly string[]): CharacterPrefab {
  const toTeamGrey = (hex: string): string => {
    const n = Number.parseInt(hex.slice(1), 16);
    const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
    const g = Math.round(255 * Math.min(0.95, 0.45 + 0.55 * lum));
    const h = g.toString(16).padStart(2, '0');
    return `#${h}${h}${h}`;
  };
  return {
    ...prefab,
    parts: prefab.parts.map((part) =>
      partNames.includes(part.name)
        ? {
            ...part,
            voxels: part.voxels.map((v) =>
              v.metalness || v.emissive ? v : { ...v, color: toTeamGrey(v.color), team: true },
            ),
          }
        : part,
    ),
  };
}

/** Which clothing parts carry the owner's colour on the bespoke human prefabs. */
const TEAM_PARTS: Readonly<Record<string, readonly string[]>> = {
  paladin: ['cape'],
  templar: ['surcoat'],
  zealot: ['torso_robed'],
  mage: ['robe'],
  archmage: ['robe'],
  spy: ['cloak'],
  explorer: ['torso_leather'],
  engineer: ['torso'],
  settler: ['torso'],
  bronze_smith: ['apron'],
  luminarch: ['robe_skirt'],
  dark_templar: ['cape_crimson'],
  // Crimson robe stays (identity); the stole carries the team colour.
  blood_priest: ['stole'],
};

const BASE_PREFABS_EXTRA: Readonly<Record<string, CharacterPrefab>> = {
  soldier,
  bowman,
  knight,
  catapult,
  cleric,
  settler,
  bronze_smith: bronzeSmith,
  engineer,
  guard,
  paladin,
  archmage,
  treant,
  chaos_lord: chaosLord,
  wagon,
  templar,
  mage,
  demon,
  spy,
  zealot,
  blood_priest: bloodPriest,
  seraphim,
  luminarch,
  dark_templar: darkTemplar,
  explorer,
};

export const CREATURE_PREFABS_EXTRA: Readonly<Record<string, CharacterPrefab>> = Object.fromEntries(
  Object.entries(BASE_PREFABS_EXTRA).map(([id, prefab]) => {
    const parts = TEAM_PARTS[id];
    return [id, parts ? withTeamParts(prefab, parts) : prefab];
  }),
);
